import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Modal, ModalHeader } from '~/components/overlays';
import { Button, Input } from '~/components/ui';
import {
  SellerContactsRow,
  type SellerContactsRowRecipients,
} from '~/components/common/SellerContactsRow';
import { SellerOrganizationPicker } from '~/components/common/SellerOrganizationPicker';
import { useBuyListSellerPickerLabels } from '~/components/common/useBuyListSellerPickerLabels';
import { useSaveSellerList, type SellerList } from '~/api/sellerLists';
import { toastGraphQLError } from '~/utils';

type SellerListModalProps = {
  sellerList: SellerList | null;
  onClose: () => void;
};

const NAME_MAX_LENGTH = 255;

type SavedContactIdsBySeller = Map<string, Set<string>>;

export function SellerListModal({
  sellerList,
  onClose,
}: Readonly<SellerListModalProps>) {
  const { t } = useTranslation('organizations');
  const {
    id,
    name: savedName = '',
    members: savedMembers = [],
  }: Partial<SellerList> = sellerList ?? {};
  const [name, setName] = useState(savedName);
  const [savedContactIdsBySeller] = useState<SavedContactIdsBySeller>(
    () =>
      new Map(
        savedMembers.map(({ seller, contacts }) => [
          seller.id,
          new Set(contacts.map((contact) => contact.id)),
        ])
      )
  );
  const [contactIdsBySeller, setContactIdsBySeller] = useState(
    savedContactIdsBySeller
  );
  const selectedIds = useMemo(
    () => [...contactIdsBySeller.keys()],
    [contactIdsBySeller]
  );
  const { mutateAsync: saveSellerList, isPending } = useSaveSellerList();
  const toggleSeller = (sellerId: string) =>
    setContactIdsBySeller((previous) => {
      const next = new Map(previous);
      if (!next.delete(sellerId)) {
        next.set(sellerId, new Set());
      }
      return next;
    });

  const setSellerContacts = (sellerId: string, contactIds: string[]) =>
    setContactIdsBySeller((previous) =>
      new Map(previous).set(sellerId, new Set(contactIds))
    );

  const recipients: SellerContactsRowRecipients = {
    isOrganizationSelected: (sellerId) => contactIdsBySeller.has(sellerId),
    toggleOrganization: ({ id: sellerId }) => toggleSeller(sellerId),
    contactSelectionOf: (sellerId) => ({
      contactIds: [...(contactIdsBySeller.get(sellerId) ?? [])],
      primaryContactId: undefined,
    }),
    chooseContacts: ({ id: sellerId }, contacts) =>
      setSellerContacts(
        sellerId,
        contacts.map((contact) => contact.id)
      ),
  };

  const pickerLabels = useBuyListSellerPickerLabels({
    loading: t('organizations.sellerLists.sellersLoading'),
    error: t('organizations.sellerLists.sellersError'),
    emptyMessage: t('organizations.sellerLists.noSellers'),
  });

  const rowLabels = {
    contactCount: (count: number) =>
      t('organizations.sellerLists.contactCount', { count }),
    noContacts: t('organizations.sellerLists.noContacts'),
    selectedCount: (count: number) =>
      t('organizations.sellerLists.selectedContactCount', { count }),
    selectOrganization: (sellerName: string) =>
      t('organizations.sellerLists.selectSeller', { name: sellerName }),
  };

  const save = async () => {
    const members = [...contactIdsBySeller].map(
      ([sellingOrganizationId, contactUserIds]) => ({
        sellingOrganizationId,
        contactUserIds: [...contactUserIds],
        useDefaultContacts:
          contactUserIds.size === 0 &&
          (savedContactIdsBySeller.get(sellingOrganizationId) ?? new Set())
            .size > 0,
      })
    );
    try {
      await saveSellerList({ id, name, members });
      toast.success(t('organizations.sellerLists.saved'));
      onClose();
    } catch (error) {
      toastGraphQLError(error);
    }
  };

  return (
    <Modal isOpen onClose={onClose} canClose={!isPending}>
      <div className="bg-neut-light rounded-lg shadow-xl w-[min(640px,90vw)] h-[min(640px,90vh)] flex flex-col">
        <ModalHeader
          title={
            sellerList
              ? t('organizations.sellerLists.editTitle')
              : t('organizations.sellerLists.createTitle')
          }
          onClose={onClose}
          closeDisabled={isPending}
        />
        <div className="px-6 py-4 flex flex-col gap-2 shrink-0">
          <Input
            label={t('organizations.sellerLists.name')}
            name="name"
            value={name}
            onChange={(_, value) => setName(value)}
            required
            maxLength={NAME_MAX_LENGTH}
          />
          <p className="text-sm text-neut-sec">
            {t('organizations.sellerLists.selectedCount', {
              count: selectedIds.length,
            })}
            {' · '}
            {t('organizations.sellerLists.contactsHint')}
          </p>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-2">
          <SellerOrganizationPicker
            testId="seller-list"
            pinnedOrganizationIds={selectedIds}
            labels={pickerLabels}
            renderOrganization={(organization, countAddedContact) => (
              <SellerContactsRow
                organization={organization}
                recipients={recipients}
                labels={rowLabels}
                testId={`seller-list-seller-row-${organization.id}`}
                onContactAdded={countAddedContact}
                primaryContactMode="off"
                disabled={isPending}
              />
            )}
          />
        </div>
        <div className="px-6 py-4 border-t border-neut-border flex justify-end gap-3">
          <Button
            size="sm"
            width="auto"
            variant="accent"
            onClick={onClose}
            disabled={isPending}
          >
            {t('organizations.sellerLists.cancel')}
          </Button>
          <Button
            size="sm"
            width="auto"
            onClick={() => void save()}
            loading={isPending}
            disabled={name.trim() === '' || selectedIds.length === 0}
            data-testid="seller-list-save"
          >
            {t('organizations.sellerLists.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
